import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import { getPost, Post } from "../services/postService";
import { MDXContent, TableOfContents, generateSlug } from "../components/content";
import type { TOCSection } from "../components/content";
import { motion } from "framer-motion";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sections, setSections] = useState<TOCSection[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);

  // Extract sections from rendered content
  useEffect(() => {
    if (!post || !contentRef.current) return;

    // Delay to ensure MDX content is fully rendered
    const timer = setTimeout(() => {
      if (!contentRef.current) return;
      
      const allHeadings = contentRef.current.querySelectorAll("h2, h3");
      
      if (!allHeadings || allHeadings.length === 0) return;
      
      const extractedSections: TOCSection[] = [];
      allHeadings.forEach((heading) => {
        const text = heading.textContent || "";
        let id = heading.id;
        const level = heading.tagName === "H2" ? 2 : 3;
        
        // Generate and set ID if it doesn't exist
        if (!id && text) {
          id = generateSlug(text);
          heading.id = id;
        }
        
        if (id && text) {
          extractedSections.push({ id, title: text, level });
        }
      });
      
      setSections(extractedSections);
    }, 500);

    return () => clearTimeout(timer);
  }, [post]);

  useEffect(() => {
    const loadPost = async () => {
      if (!slug) return;

      setIsLoading(true);
      setError(null);

      try {
        const fetchedPost = await getPost(slug);

        if (fetchedPost) {
          setPost(fetchedPost);
        } else {
          setError("Post not found.");
        }
      } catch (err) {
        console.error("Error loading post:", err);
        setError("Failed to load post.");
      } finally {
        setIsLoading(false);
      }
    };

    loadPost();
  }, [slug]);

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    const [year, month, day] = dateString.split("-").map(Number);
    const localDate = new Date(year, month - 1, day);
    return localDate.toLocaleDateString("en-US", options);
  };

  return (
    <Layout>
      {/* SEO Meta Tags */}
      {post && (
        <SEO
          title={post.seo?.metaTitle || post.title}
          description={post.seo?.metaDescription || post.excerpt || `Read "${post.title}" by Bekah June - insights on technology, startups, and personal growth.`}
          type="article"
          publishedTime={post.date}
          modifiedTime={post.seo?.modifiedDate}
          author={post.seo?.author || "Bekah June"}
          tags={post.tags}
          section={post.seo?.section || "Technology"}
          image={post.seo?.ogImage || post.coverImage || undefined}
          imageAlt={post.seo?.ogImageAlt || post.title}
          url={`/blog/${slug}`}
          canonical={post.seo?.canonical}
          keywords={post.seo?.keywords}
          readingTime={post.readingTime}
          wordCount={post.wordCount}
          noIndex={post.seo?.noIndex}
        />
      )}
      <div className="layout pb-[4.5rem]">
        {isLoading ? (
          <div className="max-w-2xl mx-auto animate-pulse">
            <div className="h-10 bg-beige rounded w-3/4 mb-2"></div>
            <div className="h-6 bg-beige rounded w-1/4 mb-8"></div>
            <div className="h-96 bg-beige rounded w-full mb-8"></div>
            <div className="space-y-4">
              <div className="h-6 bg-beige rounded w-full"></div>
              <div className="h-6 bg-beige rounded w-5/6"></div>
              <div className="h-6 bg-beige rounded w-4/6"></div>
            </div>
          </div>
        ) : error ? (
          <div className="max-w-2xl mx-auto text-center bg-red-50 p-8 rounded">
            <h2 className="font-serif text-2xl text-red-700 mb-4">Error</h2>
            <p className="text-red-600">{error}</p>
            <Link to="/" className="btn mt-6 inline-block">
              Return Home
            </Link>
          </div>
        ) : post ? (
          <motion.div
            className="max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <header className="mb-10 md:mt-6">
              <h1 className="font-system text-[24px] sm:text-[28px] md:text-[32px] font-semibold leading-[1.2] tracking-tight mb-5 text-[#000000]">
                {post.title}
              </h1>
              <p className="font-system text-sm tracking-wide text-[#000000]/55">
                {formatDate(post.date)}
              </p>
            </header>

            {post.coverImage && (
              <figure className="mb-12">
                <img
                  src={post.coverImage}
                  alt={post.title}
                  className="w-full h-auto rounded-lg"
                />
              </figure>
            )}

            {/* Table of Contents for longer posts with sections */}
            {sections.length > 0 && (
              <TableOfContents sections={sections} className="mb-8" />
            )}

            <div ref={contentRef}>
              <MDXContent>
                <post.Content />
              </MDXContent>
            </div>
          </motion.div>
        ) : null}
      </div>
    </Layout>
  );
};

export default BlogPost;
